import { useState } from 'react';
import { Settings } from '../lib/settings';
import { trackInterest } from '../lib/track';

// Mirrors the structure of the original site's preferences panel, but most
// toggles are disabled with a "soon" marker — they're aspirational until the
// generator learns them. Live toggles today: show_sunrise, show_sunset,
// use_emoji, event_duration_minutes.

interface Props {
  value: Settings;
  onChange: (patch: Partial<Settings>) => void;
  disabled?: boolean;
}

interface RowProps {
  id: string;
  label: string;
  checked: boolean;
  available: boolean;
  onChange?: (checked: boolean) => void;
}

function Row({ id, label, checked, available, onChange }: RowProps) {
  const [voted, setVoted] = useState(false);

  const castVote = () => {
    if (voted) return;
    setVoted(true);
    trackInterest(id);
  };

  return (
    <div className={`pref-row${available ? '' : ' disabled'}${voted ? ' voted' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {available ? (
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
        />
      ) : voted ? (
        <span className="vote-done">Feedback taken</span>
      ) : (
        <button
          type="button"
          className="vote-button"
          onClick={castVote}
          aria-label={`I want this: ${label}`}
        >
          <span className="vote-rest">Soon</span>
          <span className="vote-hover">I want this!</span>
        </button>
      )}
    </div>
  );
}

export function PreferencesPanel({ value, onChange, disabled }: Props) {
  const ro = disabled === true;

  return (
    <div className="card">
      {/* General */}
      <div className="pref-section">
        <p className="section-label">General</p>
        <Row
          id="pref-use-emoji"
          label="Use emoji (🌅 / 🌇)"
          checked={value.use_emoji}
          available={!ro}
          onChange={(v) => onChange({ use_emoji: v })}
        />
        <div className="pref-row">
          <label htmlFor="pref-duration">Event duration</label>
          <select
            id="pref-duration"
            value={value.event_duration_minutes}
            onChange={(e) =>
              onChange({ event_duration_minutes: Number(e.target.value) as Settings['event_duration_minutes'] })
            }
            disabled={ro}
            style={{ width: 'auto' }}
          >
            {[0, 1, 15, 30, 60].map((n) => (
              <option key={n} value={n}>{n} min</option>
            ))}
          </select>
        </div>
      </div>

      {/* Sun events */}
      <div className="pref-section">
        <p className="section-label">Sun events</p>
        <div className="pref-grid">
          <Row
            id="pref-sunrise"
            label="Sunrise"
            checked={value.show_sunrise}
            available={!ro}
            onChange={(v) => onChange({ show_sunrise: v })}
          />
          <Row
            id="pref-sunset"
            label="Sunset"
            checked={value.show_sunset}
            available={!ro}
            onChange={(v) => onChange({ show_sunset: v })}
          />
          <Row id="pref-golden"       label="Golden hour"         checked={false} available={false} />
          <Row id="pref-solar-noon"   label="Solar noon"          checked={false} available={false} />
          <Row id="pref-daylight"     label="Total daylight"      checked={false} available={false} />
          <Row id="pref-nadir"        label="Nadir"               checked={false} available={false} />
        </div>
      </div>

      {/* Twilight */}
      <div className="pref-section">
        <p className="section-label">Twilight</p>
        <div className="pref-grid">
          <Row id="pref-civil"        label="Civil"               checked={false} available={false} />
          <Row id="pref-nautical"     label="Nautical"            checked={false} available={false} />
          <Row id="pref-astronomical" label="Astronomical"        checked={false} available={false} />
        </div>
      </div>

      {/* Moon */}
      <div className="pref-section">
        <p className="section-label">Moon</p>
        <div className="pref-grid">
          <Row id="pref-moonrise"     label="Moonrise"            checked={false} available={false} />
          <Row id="pref-moonset"      label="Moonset"             checked={false} available={false} />
          <Row id="pref-moon-phase"   label="Daily phase"         checked={false} available={false} />
        </div>
      </div>

      {/* Weather */}
      <div className="pref-section">
        <p className="section-label">Weather</p>
        <div className="pref-grid">
          <Row id="pref-weather"      label="Daily forecast"      checked={false} available={false} />
          <Row id="pref-weather-temp" label="Show temperature"    checked={false} available={false} />
        </div>
      </div>
    </div>
  );
}
